import { useGetAddressMutation } from "apollo/hooks/mutations";
import {
  DELIVERY_ADDRESS_CLINIC_ADDRESS,
  DELIVERY_ADDRESS_NEW_ADDRESS,
  DELIVERY_ADDRESS_PATIENT_ADDRESS,
} from "core/consts";
import {
  Address,
  Auction,
  CareproviderAddress,
  EncryptedField,
  Location,
} from "core/types";
import Translations from "translations/types";

// --- formatLocation ---
export function formatLocation({
  hideStreetAndHouseNumber,
  location,
  showFloor,
  startWithZipcode,
  translations,
}: {
  hideStreetAndHouseNumber?: boolean;
  location: (Address & Location) | undefined;
  showFloor?: boolean;
  startWithZipcode?: boolean;
  translations: Translations;
}) {
  if (!location) return "";

  const hasHouseNumber = !!(
    location?.encrypted_house_number || location?.street_number
  );
  const houseNumber =
    location?.encrypted_house_number?.decrypted || location?.street_number;
  let address = "";

  if (hideStreetAndHouseNumber) {
    // Only show zipcode and city
    if (location?.zipcode) {
      address += location.zipcode;
    }
    if (location?.city) {
      address += ` ${location.city}`;
    }
    return address === "" ? null : address;
  }

  if (hasHouseNumber) {
    if (location?.zipcode) {
      address += location.zipcode;
    }
    if (location?.city) {
      address += ` ${location.city}, `;
    }
    if (location?.street && houseNumber) {
      address += `${location.street} ${houseNumber}`;
    } else if (location?.street) {
      address += `${location.street}`;
    }
    if (location?.floor?.length && showFloor) {
      address += ` ${translations.patient.patientInformation.floor} ${location.floor}`;
    }
  } else if (startWithZipcode) {
    if (location?.zipcode) {
      address += location.zipcode;
      if (location?.city) {
        address += location?.street
          ? ` ${location.city}, ${location.street}`
          : ` ${location.city}`;
      }
    }
  } else {
    if (location?.zipcode && location?.street) {
      address += `${location.street}, `;
    }
    if (location?.zipcode) {
      address += location.zipcode;
    }
    if (location?.city) {
      address += ` ${location.city}`;
    }
  }
  return address === "" ? null : address;
}

// --- formatAddress ---
export const formatAddress = (providerAddress: CareproviderAddress): string => {
  const { address, city, zip_code } = providerAddress || {};
  const components = [
    address,
    zip_code && address ? `, ${zip_code}` : zip_code,
    (zip_code || address) && city ? ` ${city}` : city,
  ].filter(Boolean);
  const addressString = components.join("");
  return addressString;
};

// --- formatMedicalSuppliesLocation ---
export function formatMedicalSuppliesLocation(
  auction: Auction | undefined | null,
): string | undefined {
  const medicalSupplies = auction?.medical_supplies;
  const deliveryType = medicalSupplies?.delivery_address_type;

  if (medicalSupplies && deliveryType) {
    switch (deliveryType) {
      case DELIVERY_ADDRESS_CLINIC_ADDRESS: {
        const careseekerAddress = auction.patient.careseeker?.address;

        if (careseekerAddress?.zipcode?.length) {
          return careseekerAddress.zipcode;
        }

        break;
      }

      case DELIVERY_ADDRESS_NEW_ADDRESS: {
        if (medicalSupplies?.delivery_address?.zipcode?.length) {
          return medicalSupplies?.delivery_address.zipcode;
        }
      }
    }
  }

  return auction?.profile?.search_location?.zipcode ?? undefined;
}

// Helper function to check if a string is likely a house number
function isLikelyHouseNumber(str: string): boolean {
  // Match patterns like: "10", "10A", "10-12", "10/12", "10B"
  return /^\d+[A-Za-z]?(-\d+)?(\/\d+)?$/.test(str);
}

// --- formatSearchLocation ---
export async function formatSearchLocation({
  city,
  getAddress,
  onError,
  postal_code,
  street_address,
}: {
  city?: string;
  getAddress?: ReturnType<typeof useGetAddressMutation>[0];
  onError?: (error: Error) => void;
  postal_code?: string;
  street_address?: string;
}): Promise<Location | undefined> {
  if (!street_address || !city || !postal_code) {
    console.error("Missing required fields in formatSearchLocation", {
      module: "kis_import",
      has_street_address: !!street_address,
      has_city: !!city,
      has_postal_code: !!postal_code,
    });
    return undefined;
  }

  const streetAddressParts = street_address.split(" ");
  const lastPart = streetAddressParts[streetAddressParts.length - 1];
  const isLastPartHouseNumber = isLikelyHouseNumber(lastPart);

  if (!isLastPartHouseNumber) {
    console.warn("No valid house number found in street address", {
      module: "kis_import",
      street_address,
      last_part: lastPart,
    });
  }

  const street = isLastPartHouseNumber
    ? streetAddressParts.slice(0, -1).join(" ")
    : street_address;
  const house_number = isLastPartHouseNumber ? lastPart : undefined;
  const zipcode = postal_code;
  let coordinates;
  let radius_in_meter;
  try {
    if (getAddress) {
      const data = await getAddress({
        city,
        street,
        zipcode,
        house_number: house_number ?? "",
        country: "DE",
      });
      coordinates = data?.data?.address?.coordinates;
      radius_in_meter = data?.data?.address?.radius;

      if (!coordinates) {
        console.warn("No coordinates returned from getAddress", {
          module: "kis_import",
          street_address,
          city,
          zipcode,
        });
      }
    }
  } catch (err) {
    console.error("Error in getAddress call", {
      module: "kis_import",
      error: err,
      street_address,
      city,
      zipcode,
    });
    onError?.(err as Error);
  }
  return {
    city,
    latitude: coordinates?.latitude,
    longitude: coordinates?.longitude,
    radius_in_meter,
    street,
    ...(house_number && {
      encrypted_house_number: { decrypted: house_number } as EncryptedField,
    }),
    zipcode,
  };
}

// --- locationValue ---
export function locationValue(
  auction: Auction | null | undefined,
  translations: Translations,
) {
  const formatLocations = (location: any) => {
    return location
      ? formatLocation({ location, translations, showFloor: true })
      : "";
  };
  switch (auction?.medical_supplies?.delivery_address_type) {
    case DELIVERY_ADDRESS_PATIENT_ADDRESS: {
      return {
        deliveryAddressString: formatLocations(
          auction.profile?.search_location,
        ),
        deliveryAddressPrefix: translations.address.patientAddress,
      };
    }
    case DELIVERY_ADDRESS_CLINIC_ADDRESS: {
      return {
        deliveryAddressString: formatLocations(
          auction.patient.careseeker?.address,
        ),
        deliveryAddressPrefix: translations.address.clinicAddress,
      };
    }
    case DELIVERY_ADDRESS_NEW_ADDRESS: {
      return {
        deliveryAddressString: formatLocations(
          auction.medical_supplies.delivery_address,
        ),
        deliveryAddressPrefix: translations.address.anotherAddress,
      };
    }
    default:
      return { deliveryAddressString: "", deliveryAddressPrefix: "" };
  }
}
